import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Crosshair, Eye, Target, Zap, Shield,
  CheckCircle, TreePine, Activity, Quote, Focus
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SKILLS = [
  {
    icon: Crosshair,
    title: 'The Pre-Shot Routine',
    description: 'The pre-shot routine is your "psychological fortress" — the 20-40 seconds that are entirely under your control. It\'s not just about alignment; it\'s about reaching a "go" state where you\'ve decided, visualised, and committed. Doubt has no place inside this routine.',
    onCourse: 'Build a 3-step routine: (1) Decide — pick your club, target, and shot shape. (2) See it — visualise the ball\'s path, trajectory, and landing. (3) Step in and trust — swing with full commitment. If doubt creeps in at any point, step back and restart.',
    offCourse: 'Practise your full routine on the range. Make the routine itself the goal, not the ball flight. Hit 10 consecutive shots with perfect routine completion — rate each one honestly.',
    inApp: 'Rate "Shot-by-shot commitment" and "Decision making quality" in your Performance Scorecard\'s During section. Track improvement over the season.',
  },
  {
    icon: Target,
    title: 'Committed Swings',
    description: 'Phil Mickelson\'s revelation before his PGA Championship win: "If you\'re going to win this thing, you\'re going to have to make committed golf swings." A committed swing isn\'t about swinging harder — it\'s about swinging with zero doubt. Full trust in your decision, your club, and your body.',
    onCourse: 'Before every swing, ask: "Am I 100% committed to this shot?" If the answer is anything less than yes, step back. Reselect or recommit. A half-committed swing is worse than a wrong club fully committed.',
    offCourse: 'On the range, rate each shot\'s commitment level out of 10 before you check where it went. You\'ll quickly see the correlation between commitment and quality contact.',
    inApp: 'Your "During the Round" scorecard scores directly reflect commitment quality. Log specific committed vs uncommitted shots in your Diary for pattern recognition.',
  },
  {
    icon: Zap,
    title: 'One Shot at a Time',
    description: 'The simplest principle in golf — and the hardest to execute. Each shot exists in its own world. The previous shot is gone. The next shot doesn\'t exist yet. Your only job is THIS shot, THIS target, THIS commitment.',
    onCourse: 'After each shot, let it go within 5 steps. Good or bad, it\'s done. Walk forward with your attention on what\'s ahead, not what\'s behind. Treat every shot as if it\'s the first shot of the round.',
    offCourse: 'Practise present-moment awareness in daily life. When eating, just eat. When walking, just walk. The more you practise single-task focus, the more available it becomes on the course.',
    inApp: 'Rate "Staying in the present moment" and "Emotional control under pressure" in your Performance Scorecards. Your In-Between score also reflects how well you\'re releasing shots.',
  },
  {
    icon: Shield,
    title: 'Acceptance of Outcomes',
    description: 'You can control your preparation, your routine, and your commitment — but not where the ball ends up. A gust of wind, an unlucky bounce, a sprinkler head — golf is full of uncontrollables. Acceptance means executing with trust and receiving the outcome without ego.',
    onCourse: 'After every shot, before you react, take one breath. Then choose your response. You always have a choice between reaction (automatic, often destructive) and response (intentional, constructive).',
    offCourse: 'Reflect on your last round. Identify 3 shots where your reaction was destructive. Visualise responding differently — with calm acceptance. Rehearse the better response.',
    inApp: 'Your Diary is perfect for processing emotional reactions to outcomes. Tag entries with the QMG "Accepting" tag when you successfully release attachment to results.',
  },
];

const ApproachDuringPage = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="pt-24 pb-20">
        <section className="bg-[#BC562B] py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-12 max-w-4xl">
            <Link to="/the-approach" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8 transition-colors" data-testid="back-link">
              <ArrowLeft className="w-4 h-4" /> Back to The Approach
            </Link>
            <p className="text-white/50 text-sm uppercase tracking-[0.2em] mb-2">Area Two</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-4">During the Round</h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
              Execution and focus. Your pre-shot routine, shot commitment, and the ability to stay fully present in each moment of play.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <section className="py-12 md:py-16" data-testid="what-it-means">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-6">What "During" Really Means</h2>
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <p className="text-[#57534E] leading-relaxed mb-4">
                "During" isn't just the 1.4 seconds of the golf swing. It encompasses the entire execution process — from the moment you begin assessing a shot to the moment you release the club. It includes your decision-making, your routine, and the critical split-second where you either commit or hesitate.
              </p>
              <p className="text-[#57534E] leading-relaxed">
                The Red Dot technique, used by Louis Oosthuizen to win the 2010 Open by 7 shots, is perhaps the most famous mental tool in modern golf. It demonstrates a key principle: <strong>you don't need to concentrate for 4 hours — you need focus for about 40 seconds per shot</strong>. The rest is recovery and preparation.
              </p>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="testimonial">
            <div className="bg-[#F2F0E9] border-l-4 border-[#BC562B] p-6 md:p-8">
              <Quote className="w-8 h-8 text-[#BC562B]/30 mb-4" />
              <blockquote className="font-['Playfair_Display'] text-lg md:text-xl text-[#1A1A1A] leading-relaxed mb-4">
                "If you're going to win this thing, you're going to have to make committed golf swings."
              </blockquote>
              <p className="text-[#737373] text-sm">— <strong>Phil Mickelson</strong>, on the mental shift before his PGA Championship victory</p>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="red-dot-section">
            <div className="bg-[#BC562B] p-6 md:p-10 text-white">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-4 text-white">The Red Dot Story</h2>
              <p className="text-white/90 leading-relaxed mb-4">
                Before the 2010 Open Championship at St Andrews, Louis Oosthuizen's concentration was, in his own words, "all over the place." His mental performance coach gave him a deceptively simple tool: a red dot on his glove.
              </p>
              <p className="text-white/90 leading-relaxed mb-4">
                The instruction was straightforward — press the dot before each shot to trigger deep, present-moment focus. It became an anchor that blocked out the noise of a major championship. Oosthuizen went on to win by <strong>7 shots</strong>, one of the most dominant Open performances in modern history.
              </p>
              <p className="text-white/60 text-sm italic">
                The red dot wasn't magic. It was a trained association between a physical trigger and a concentrated mental state — built through practice.
              </p>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="skills-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-2">Skills to Train</h2>
            <p className="text-[#57534E] mb-8">Master the moments of execution with these focus and commitment tools.</p>
            <div className="space-y-8">
              {SKILLS.map((skill, i) => (
                <React.Fragment key={i}>
                  <div className="bg-white border border-[#E7E5E4] overflow-hidden" data-testid={`skill-${i}`}>
                    <div className="p-6 md:p-8">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 bg-[#BC562B] text-white rounded-full flex items-center justify-center flex-shrink-0">
                          <skill.icon className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">{skill.title}</h3>
                          <p className="text-[#57534E] leading-relaxed">{skill.description}</p>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4 mt-6">
                        <div className="bg-[#2C5530]/5 p-4">
                          <p className="text-xs text-[#2C5530] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> On the Course</p>
                          <p className="text-sm text-[#57534E]">{skill.onCourse}</p>
                        </div>
                        <div className="bg-[#BC562B]/5 p-4">
                          <p className="text-xs text-[#BC562B] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><TreePine className="w-3.5 h-3.5" /> Off the Course</p>
                          <p className="text-sm text-[#57534E]">{skill.offCourse}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/5 p-4">
                          <p className="text-xs text-[#1A1A1A] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Track in QMG</p>
                          <p className="text-sm text-[#57534E]">{skill.inApp}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {i === 0 && (
                    <div className="bg-[#2C5530] p-6 md:p-8 text-center" data-testid="values-quote">
                      <Quote className="w-8 h-8 text-white/20 mx-auto mb-4" />
                      <blockquote className="font-['Playfair_Display'] text-lg md:text-2xl text-white font-medium leading-relaxed">
                        "When your commitment to your personal values is greater than your addiction to the outcome, your golf will improve for the better."
                      </blockquote>
                      <p className="text-white/40 uppercase tracking-[0.2em] text-xs mt-4">On Intention vs Outcome</p>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </section>

          <section className="pb-8" data-testid="nav-section">
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/the-approach/before" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#2C5530]/40 hover:shadow-md transition-all group" data-testid="nav-before">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Previous Area</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#2C5530] transition-colors">Before the Round</h3>
                <p className="text-sm text-[#57534E]">Preparation, visualisation, and mental readiness.</p>
                <ArrowLeft className="w-5 h-5 text-[#2C5530] mt-3" />
              </Link>
              <Link to="/the-approach/in-between" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#7C3AED]/40 hover:shadow-md transition-all group" data-testid="nav-in-between">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Next Area</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#7C3AED] transition-colors">In-Between Shots</h3>
                <p className="text-sm text-[#57534E]">Recovery, reset, and the walk that shapes your round.</p>
                <ArrowRight className="w-5 h-5 text-[#7C3AED] mt-3" />
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ApproachDuringPage;
